
import { useEffect, useState } from 'react';
import Button from '../../components/base/Button';

export default function AboutPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Smooth scroll to top when page loads
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Trigger hero animations after a short delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set(prev).add(entry.target.id));
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  const timeline = [
    {
      year: '2019',
      title: 'Vision Initiated',
      description: 'Concept and vision initiated by founder Omolemo Segooa'
    },
    {
      year: '2025',
      title: 'Hera Minerals Established',
      description: 'Officially established as a Segra Group subsidiary, securing operational assets and funding'
    },
    {
      year: 'Present',
      title: 'Active Operations',
      description: 'Active operations across coal, chrome, and emerging iron ore assets with clear expansion strategy'
    }
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Safety First',
      description: 'The health and safety of our employees, contractors, and communities are non-negotiable'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Environmental Stewardship',
      description: 'We minimize our environmental impact through responsible extraction, rehabilitation, and carbon reduction initiatives'
    },
    {
      icon: 'ri-heart-line',
      title: 'Integrity & Transparency',
      description: 'We operate ethically, honestly, and with accountability'
    },
    {
      icon: 'ri-team-line',
      title: 'Community Engagement',
      description: 'We build strong partnerships with local communities and traditional leaders'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation & Excellence',
      description: 'We embrace new technologies and continuously improve our operations'
    }
  ];

  const differentiators = [
    '100% Black youth- and woman-owned',
    'Strategic access to export hubs (including Richards Bay)',
    'Export-grade coal and high-purity chrome supply',
    'Diversified mineral portfolio (Coal, Chrome, Iron Ore)',
    'Strong community partnerships and DMRE compliance',
    'Built on scalable production and long-term growth strategy'
  ];

  const leadership = [
    {
      name: 'Omolemo Segooa',
      position: 'Managing Director & Founder'
    },
    {
      name: 'Vuyeya Segooa',
      position: 'Corporate Affairs Director'
    },
    {
      name: 'Bonginkosi Modlolo',
      position: 'Operations Director'
    }
  ];

  const growthTargets = [
    {
      mineral: 'Coal',
      current: '10,000',
      target: '200,000',
      unit: 'tons per month'
    },
    {
      mineral: 'Chrome',
      current: 'Starting',
      target: '50,000',
      unit: 'tons per month'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center bg-cover bg-center bg-fixed overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 29, 46, 0.7), rgba(11, 29, 46, 0.7)), url('https://public.readdy.ai/ai/img_res/4c4ebba8d0bfb7cfa529e4182f26b1ef.jpg')`
        }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <h1 
            className="text-5xl md:text-7xl font-bold text-[#2DD4BF] mb-6 animate-fade-in-up"
            style={{ fontFamily: '"Montserrat", sans-serif' }}
          >
            Forged in Resilience. Driven by Opportunity.
          </h1>
          <p 
            className="text-xl md:text-2xl text-[#E9E3D0] mb-8 animate-fade-in-up animation-delay-300"
            style={{ fontFamily: '"Open Sans", sans-serif' }}
          >
            Pure. Natural. Essential.
          </p>
          <div className="animate-fade-in-up animation-delay-700">
            <Button 
              size="lg" 
              href="/operations"
              className="bg-[#2DD4BF] text-[#0B1E2E] hover:bg-[#14B8A6] hover:shadow-xl px-8 py-4 text-lg rounded-xl hover:scale-105 hover:shadow-xl transition-all duration-300 pulse-glow"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Discover Our Story
            </Button>
          </div>
        </div>
        
        {/* Animated background overlay */}
        <div 
          className={`absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0B1D2E]/20 transition-opacity duration-2000 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        ></div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div 
              id="overview-text"
              data-animate
              className={`transition-all duration-1000 ease-out ${
                visibleSections.has('overview-text') 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 -translate-x-10'
              }`}
            >
              <h2 
                className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6 hover:text-[#2DD4BF] transition-colors duration-300"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                About Hera Minerals
              </h2>
              <p 
                className="text-lg text-[#0B1D2E] mb-6 leading-relaxed"
                style={{ fontFamily: '"Open Sans", sans-serif' }}
              >
                Hera Minerals is a modern, 100% black youth- and woman-owned mining company committed to responsibly sourcing and delivering the minerals that fuel progress across Africa and beyond. Established in 2025 as a subsidiary of Segra Group, Hera Minerals was founded on the belief that mining should create shared value — empowering communities, protecting the environment, and driving economic growth.
              </p>
              <p 
                className="text-lg text-[#0B1D2E] mb-8 leading-relaxed"
                style={{ fontFamily: '"Open Sans", sans-serif' }}
              >
                From our operational footprint across key mineral-rich regions in South Africa to our long-term expansion strategy, Hera Minerals is positioned to become a new force in African mining — agile, innovative, socially inclusive, and future-focused.
              </p>
              <Button 
                variant="primary" 
                size="lg"
                href="/operations"
                className="bg-[#2DD4BF] text-[#0B1D2E] hover:bg-[#14B8A6] hover:scale-105 hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                Learn About Our Operations
              </Button>
            </div>
            <div 
              id="overview-image"
              data-animate
              className={`relative transition-all duration-1000 ease-out delay-300 ${
                visibleSections.has('overview-image') 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-10'
              }`}
            >
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20African%20mining%20team%2C%20diverse%20group%20of%20young%20professionals%20in%20mining%20safety%20gear%2C%20modern%20mining%20facility%20background%2C%20corporate%20photography%2C%20professional%20lighting%2C%20high%20quality&width=600&height=800&seq=about-team&orientation=portrait"
                alt="Hera Minerals Team"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#2DD4BF] p-6 rounded-xl shadow-xl hover:scale-110 transition-transform duration-300">
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#0B1D2E]" style={{ fontFamily: '"Montserrat", sans-serif' }}>100%</p>
                  <p className="text-sm text-[#0B1D2E]" style={{ fontFamily: '"Open Sans", sans-serif' }}>Black Youth & Woman Owned</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-[#E9E3D0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="timeline-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('timeline-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6 hover:text-[#2DD4BF] transition-colors duration-300"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Our Story
            </h2>
            <p 
              className="text-xl text-[#0B1D2E] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Our journey began with a bold vision to reshape the mining landscape in Africa — not just by extracting minerals, but by building opportunities for people and contributing to sustainable development.
            </p>
          </div>
          
          <div 
            id="timeline-content"
            data-animate
            className={`relative transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('timeline-content') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-[#2DD4BF]"></div>
            <div className="space-y-16">
              {timeline.map((item, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className="w-1/2 px-8">
                    <div className={`${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                      <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-500">
                        <h3 
                          className="text-3xl font-bold text-[#2DD4BF] mb-2"
                          style={{ fontFamily: '"Montserrat", sans-serif' }}
                        >
                          {item.year}
                        </h3>
                        <h4 
                          className="text-xl font-semibold text-[#0B1D2E] mb-3"
                          style={{ fontFamily: '"Montserrat", sans-serif' }}
                        >
                          {item.title}
                        </h4>
                        <p 
                          className="text-[#0B1D2E]"
                          style={{ fontFamily: '"Open Sans", sans-serif' }}
                        >
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="w-6 h-6 bg-[#2DD4BF] rounded-full border-4 border-white shadow-lg relative z-10 hover:scale-125 transition-transform duration-300"></div>
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="vision-mission"
            data-animate
            className={`grid grid-cols-1 lg:grid-cols-2 gap-16 transition-all duration-1000 ease-out ${
              visibleSections.has('vision-mission') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="bg-white p-12 rounded-2xl shadow-xl hover-shadow-2xl hover:scale-105 transition-all duration-500">
              <div className="w-16 h-16 bg-[#2DD4BF] rounded-full flex items-center justify-center mb-6 hover:scale-110 transition-transform duration-300">
                <i className="ri-eye-line text-2xl text-[#0B1D2E]"></i>
              </div>
              <h3 
                className="text-3xl font-bold text-[#0B1D2E] mb-6 hover:text-[#2DD4BF] transition-colors duration-300"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                Our Vision
              </h3>
              <p 
                className="text-lg text-[#0B1D2E] leading-relaxed"
                style={{ fontFamily: '"Open Sans", sans-serif' }}
              >
                To become a leading mining company in Africa, known for sustainable practices, operational excellence, and meaningful community development.
              </p>
            </div>
            
            <div className="bg-white p-12 rounded-2xl shadow-xl hover-shadow-2xl hover:scale-105 transition-all duration-500">
              <div className="w-16 h-16 bg-[#14B8A6] rounded-full flex items-center justify-center mb-6 hover:scale-110 transition-transform duration-300">
                <i className="ri-target-line text-2xl text-white"></i>
              </div>
              <h3 
                className="text-3xl font-bold text-[#0B1D2E] mb-6 hover:text-[#2DD4BF] transition-colors duration-300"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                Our Mission
              </h3>
              <p 
                className="text-lg text-[#0B1D2E] leading-relaxed"
                style={{ fontFamily: '"Open Sans", sans-serif' }}
              >
                To responsibly extract and produce minerals that fuel progress and improve lives, while prioritizing safety, environmental protection, innovative solutions, and community well-being.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-[#0B1D2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="values-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('values-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#2DD4BF] mb-6 hover:text-white transition-colors duration-300"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Our Core Values
            </h2>
            <p 
              className="text-xl text-[#E9E3D0] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              These values form the foundation of every decision we make
            </p>
          </div>
          
          <div 
            id="values-grid"
            data-animate
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('values-grid') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            {values.map((value, index) => (
              <div key={index} className="bg-[#E9E3D0]/10 backdrop-blur-sm p-8 rounded-xl border border-[#2DD4BF]/20 hover:border-[#2DD4BF]/50 hover:scale-105 transition-all duration-500">
                <div className="w-16 h-16 bg-[#2DD4BF] rounded-full flex items-center justify-center mb-6 mx-auto hover:scale-110 transition-transform duration-300">
                  <i className={`${value.icon} text-2xl text-[#0B1D2E]`}></i>
                </div>
                <h3 
                  className="text-xl font-bold text-[#2DD4BF] mb-4 text-center hover:text-white transition-colors duration-300"
                  style={{ fontFamily: '"Montserrat", sans-serif' }}
                >
                  {value.title}
                </h3>
                <p 
                  className="text-[#E9E3D0] text-center leading-relaxed"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 bg-[#E9E3D0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="differentiators-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('differentiators-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6 hover:text-[#2DD4BF] transition-colors duration-300"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              What Sets Us Apart
            </h2>
            <p 
              className="text-xl text-[#0B1D2E] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Hera Minerals is more than a mining company — we are a catalyst for transformation
            </p>
          </div>
          
          <div 
            id="differentiators-grid"
            data-animate
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('differentiators-grid') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-500">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-[#2DD4BF] rounded-full flex items-center justify-center flex-shrink-0 mt-1 hover:scale-110 transition-transform duration-300">
                    <i className="ri-check-line text-[#0B1D2E] text-lg"></i>
                  </div>
                  <p 
                    className="text-[#0B1D2E] font-medium leading-relaxed"
                    style={{ fontFamily: '"Open Sans", sans-serif' }}
                  >
                    {item}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="leadership-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('leadership-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6 hover:text-[#2DD4BF] transition-colors duration-300"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Leadership
            </h2>
            <p 
              className="text-xl text-[#0B1D2E] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Hera Minerals is led by a dynamic, experienced, and visionary leadership team with collective expertise spanning mining operations, stakeholder relations, governance, and project execution
            </p>
          </div>
          
          <div 
            id="leadership-grid"
            data-animate
            className={`grid grid-cols-1 md:grid-cols-3 gap-12 transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('leadership-grid') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            {leadership.map((leader, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-xl p-8 text-center hover:shadow-2xl hover:scale-105 transition-all duration-500">
                <div className="w-24 h-24 bg-[#2DD4BF] rounded-full flex items-center justify-center mx-auto mb-6 hover:scale-110 transition-transform duration-300">
                  <i className="ri-user-line text-3xl text-[#0B1D2E]"></i>
                </div>
                <h3 
                  className="text-2xl font-bold text-[#0B1D2E] mb-2 hover:text-[#2DD4BF] transition-colors duration-300"
                  style={{ fontFamily: '"Montserrat", sans-serif' }}
                >
                  {leader.name}
                </h3>
                <p 
                  className="text-[#2DD4BF] font-medium"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  {leader.position}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Growth Targets */}
      <section className="py-20 bg-[#0B1D2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="growth-targets-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('growth-targets-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#2DD4BF] mb-6 hover:text-white transition-colors duration-300"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              A Future of Growth and Impact
            </h2>
            <p 
              className="text-xl text-[#E9E3D0] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              By 2026, Hera Minerals will scale production significantly with investments into wash plants, logistics, and expansion infrastructure
            </p>
          </div>
          
          <div 
            id="growth-targets-grid"
            data-animate
            className={`grid grid-cols-1 md:grid-cols-2 gap-12 transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('growth-targets-grid') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            {growthTargets.map((target, index) => (
              <div key={index} className="bg-[#E9E3D0]/10 backdrop-blur-sm p-12 rounded-2xl border border-[#2DD4BF]/20 text-center hover:border-[#2DD4BF]/50 hover:scale-105 transition-all duration-500">
                <h3 
                  className="text-3xl font-bold text-[#2DD4BF] mb-6 hover:text-white transition-colors duration-300"
                  style={{ fontFamily: '"Montserrat", sans-serif' }}
                >
                  {target.mineral}
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-[#E9E3D0] text-sm mb-1" style={{ fontFamily: '"Open Sans", sans-serif' }}>Current</p>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: '"Montserrat", sans-serif' }}>{target.current}</p>
                  </div>
                  <div className="w-16 h-1 bg-[#2DD4BF] mx-auto"></div>
                  <div>
                    <p className="text-[#E9E3D0] text-sm mb-1" style={{ fontFamily: '"Open Sans", sans-serif' }}>Target</p>
                    <p className="text-4xl font-bold text-[#2DD4BF]" style={{ fontFamily: '"Montserrat", sans-serif' }}>{target.target}</p>
                  </div>
                  <p className="text-[#E9E3D0] text-sm" style={{ fontFamily: '"Open Sans", sans-serif' }}>{target.unit}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div 
            id="growth-quote"
            data-animate
            className={`text-center mt-16 transition-all duration-1000 ease-out delay-500 ${
              visibleSections.has('growth-quote') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <p 
              className="text-xl text-[#E9E3D0] italic"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              We believe in mining that leaves a lasting positive legacy — in people, in communities, and in the environment.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2DD4BF]">
        <div 
          id="about-cta"
          data-animate
          className={`max-w-4xl mx-auto px-6 lg:px-8 text-center transition-all duration-1000 ease-out ${
            visibleSections.has('about-cta') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 
            className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6 hover:text-white transition-colors duration-300"
            style={{ fontFamily: '"Montserrat", sans-serif' }}
          >
            Ready to Get in Touch?
          </h2>
          <p 
            className="text-xl text-[#0B1D2E] mb-8"
            style={{ fontFamily: '"Open Sans", sans-serif' }}
          >
            Discover how Hera Minerals can contribute to your success
          </p>
          <div className="flex justify-center">
            <Button 
              variant="secondary" 
              size="lg" 
              href="/contact" 
              className="bg-[#0B1D2E] text-white hover:bg-[#0B1D2E]/90 hover:scale-105 hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Get in Touch
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
